import { useEffect, useState } from 'react'
import { Header } from '../components/Header'
import { Footer } from '../components/Footer'
import { Timeline } from '../components/Timeline'
import { Faq } from '../components/Faq'
import { GoogleG } from '../components/GoogleG'
import { useTitle } from '../lib/title'
import { Calendar } from '../components/Calendar'

const BOT = 'bot@calendarfeedback.com'

export default function Landing() {
  useTitle()
  const [email, setEmail] = useState<string | null>(null)

  useEffect(() => {
    fetch('/api/me')
      .then(r => r.ok ? r.json() : null)
      .then(d => setEmail(d?.email ?? null))
      .catch(() => setEmail(null))
  }, [])

  return (
    <>
      <Header>
        <a href="#how-title">How it works</a>
        <a href="/privacy">Privacy</a>
        {email
          ? <a className="btn" href="/dashboard">Dashboard</a>
          : <a className="btn" href="/auth/google/start">Sign in</a>}
      </Header>

      <main>
        <section className="wrap hero">
          <div className="hero-copy">
            <h1>Every meeting ends with a <em>secret ballot.</em></h1>
            <p className="lede">
              Invite the bot to a Google Calendar meeting. Afterwards everyone taps one of
              three buttons, nobody sees who tapped what, and the meeting gets a score you
              can watch week by week.
            </p>
            <CopyAddress />
            <p className="hint">Free. No account needed to vote.</p>
          </div>
          <figure className="hero-art">
            <img
              src="/img/hero.webp"
              srcSet="/img/hero-900.webp 900w, /img/hero.webp 1800w"
              sizes="(max-width: 860px) 100vw, 60vw"
              width={1800}
              height={1195}
              alt="A paper model of a meeting room: chairs around a table, and a small blue ballot box with a card posted in it."
              fetchPriority="high"
            />
          </figure>
        </section>

        <Timeline />

        <section className="wrap section" aria-labelledby="product-title">
          <div className="product-head">
            <h2 id="product-title">One number per meeting, <em>week after week.</em></h2>
            <p>A recurring meeting gets a score every time it runs. A number that climbs, or slides, says what nobody says out loud.</p>
          </div>
          <Calendar />
        </section>

        <section className="wrap section anon" aria-labelledby="anon-title">
          <img src="/img/figures.webp" width={1400} height={930} loading="lazy" alt="Three small paper figures at a table, each holding up a card: thumbs up, neutral, thumbs down." />
          <div>
            <h2 id="anon-title">Anonymous by design, <em>not by promise.</em></h2>
            <div className="proofs">
              <div className="proof">
                <h3>Addresses are hashed</h3>
                <p>We keep a one-way hash of each attendee's address, never the address itself. Enough to send one link per person and nothing more.</p>
              </div>
              <div className="proof">
                <h3>Votes are unlinked</h3>
                <p>A vote is stored against the meeting, not against the person who cast it. There is no join to run, even for us.</p>
              </div>
              <div className="proof">
                <h3>Three before anything</h3>
                <p>No score exists until three people vote, and the organiser only ever sees the total. A team of two cannot be singled out.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="wrap section trend" aria-labelledby="signin-title">
          <img src="/img/trend.webp" width={1400} height={930} loading="lazy" alt="Ten paper strips rising in height from left to right like a bar chart, the last one blue." />
          <div>
            <h2 id="signin-title">See your scores <em>over time.</em></h2>
            {email ? (
              <p>Signed in as {email}.</p>
            ) : (
              <p>Optional. Sign in to see scores for your past meetings and the upcoming ones where the bot is invited.</p>
            )}
            <div className="cta">
              {email ? (
                <a className="btn solid" href="/dashboard">Open dashboard</a>
              ) : (
                <a className="gbtn" href="/auth/google/start"><GoogleG />Sign in with Google</a>
              )}
            </div>
          </div>
        </section>

        <Faq />
      </main>
      <Footer />
    </>
  )
}

function CopyAddress() {
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!done) return
    const t = setTimeout(() => setDone(false), 2200)
    return () => clearTimeout(t)
  }, [done])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(BOT)
      setDone(true)
    } catch {
      // Clipboard blocked (http, permissions): select the text instead so a
      // manual copy still works.
      const range = document.createRange()
      const el = document.getElementById('bot-address')
      if (el) {
        range.selectNodeContents(el)
        const sel = window.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(range)
      }
    }
  }

  return (
    <div className="address">
      <code id="bot-address">{BOT}</code>
      <button type="button" onClick={copy} data-done={done} aria-live="polite">
        {done ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}
